import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useBooking } from '../context/BookingContext';
import './Plans.css';

const Plans = () => {
  const { openBooking } = useBooking();
  const [plans, setPlans] = useState([]);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all'); // all, service_plan, course_plan, combo_plan
  const [apiErrors, setApiErrors] = useState([]);

  useEffect(() => {
    loadPlansData();
  }, []);

  const loadPlansData = async () => {
    try {
      setLoading(true);
      const errors = [];
      const [plansResponse, pageResponse] = await Promise.allSettled([
        apiService.getPlans(),
        apiService.getPage('plans')
      ]);

      if (plansResponse.status === 'fulfilled') {
        const raw = plansResponse.value.data;
        const plansData = Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw) ? raw : []);
        console.log('[PLANS] Plans from DB:', plansData.length, plansData);
        setPlans(plansData);
      } else {
        console.error('[PLANS] Plans API failed:', plansResponse.reason);
        errors.push('Plans: ' + (plansResponse.reason?.message || 'API unreachable'));
      }

      if (pageResponse.status === 'fulfilled') {
        const raw = pageResponse.value.data;
        const pd = raw?.data || (raw?.success === false ? null : raw) || null;
        console.log('[PLANS] Page content from DB:', pd);
        setPageData(pd);
      } else {
        console.error('[PLANS] Page API failed:', pageResponse.reason);
        errors.push('Page content: ' + (pageResponse.reason?.message || 'API unreachable'));
      }

      setApiErrors(errors);
    } catch (err) {
      console.error('[PLANS] Unexpected error:', err);
      setApiErrors(['Unexpected: ' + err.message]);
    } finally {
      setLoading(false);
    }
  };

  const handleEnquiry = (planName) => {
    openBooking({
      title: 'Plan Enquiry',
      planName,
      type: 'plan_enquiry'
    });
  };

  // Group plans by type
  const groupedPlans = {
    service_plan: plans.filter(p => p.plan_type === 'service_plan'),
    course_plan: plans.filter(p => p.plan_type === 'course_plan'),
    combo_plan: plans.filter(p => p.plan_type === 'combo_plan'),
    subscription: plans.filter(p => p.plan_type === 'subscription')
  };

  // Filter plans based on selected type
  const filteredPlans = selectedType === 'all' 
    ? plans 
    : plans.filter(p => p.plan_type === selectedType);

  const pageTitle = pageData?.title || pageData?.page_name || "Workshops, Courses & Learning";
  const pageSubtitle = pageData?.excerpt || pageData?.meta_description || "Educational programmes across holistic and spiritual knowledge.";

  // Format price display
  const formatPrice = (plan) => {
    const currency = plan.currency || 'INR';
    const symbol = currency === 'INR' ? '₹' : currency === 'USD' ? '$' : currency;
    let price = `${symbol}${plan.price}`;
    
    if (plan.discounted_price && plan.discounted_price < plan.price) {
      price += ` (${plan.discount_percentage}% off)`;
    }
    
    if (plan.billing_cycle !== 'one_time') {
      price += ` / ${plan.billing_cycle}`;
    }
    
    return price;
  };

  // Get plan items summary
  const getPlanItemsSummary = (plan) => {
    if (!plan.items || plan.items.length === 0) {
      return plan.features || [];
    }
    
    const items = plan.items.map(item => {
      const name = item.item_display_name || item.item_name || 'Item';
      const qty = item.is_unlimited ? 'Unlimited' : item.quantity;
      return `${name} (${qty})`;
    });
    
    return items;
  };

  const learningProgrammes = [
    { name: 'Bhrigu Nandi Nadi Astrology', detail: 'A systematic study of planetary connections, combinations and significations.' },
    { name: 'Vedic Astrology', detail: 'A reflective way to understand life patterns, cycles and possibilities.' },
    { name: 'AstroTarot', detail: 'Tarot and astrological understanding, explored together as a reflective tool.' },
    { name: 'Tarot', detail: 'A visual and intuitive framework for exploring emotions, thoughts and circumstances.' },
    { name: 'Vedic Numerology', detail: 'Numerical patterns and their relationship with different aspects of life.' },
    { name: 'Vastu', detail: 'The relationship between space, directions, elements and energy flow.' },
    { name: 'Meditation', detail: 'Practices for calmness, awareness and inner connection.' },
    { name: 'Pranayama', detail: 'Conscious breathing practices that support the mind as well as the body.' },
    { name: 'Vedic Wellness', detail: 'Food and lifestyle through Ayurveda, doshas, seasons and individual constitution.' },
    { name: 'Spiritual practices', detail: 'Mantra, devotion and conscious intention as tools for inner growth.' }
  ];

  // Fallback only when the API is unreachable
  const fallbackPlans = [];

  const displayPlans = plans.length > 0 ? filteredPlans : fallbackPlans;

  return (
    <div className="plans-page">
      {apiErrors.length > 0 && (
        <div style={{background:'#fff3cd', border:'1px solid #ffc107', padding:'12px 20px', fontSize:'13px', color:'#856404'}}>
          <strong>⚠️ Database not connected — showing static content.</strong> API Errors: {apiErrors.join(' | ')}
          {' '}<a href="http://localhost/api/diagnostic.php" target="_blank" rel="noreferrer" style={{color:'#0d6efd'}}>Run Diagnostic</a>
          {' | '}<a href="http://localhost/api/setup_schema.php?confirm=yes" target="_blank" rel="noreferrer" style={{color:'#dc3545'}}>Setup Database</a>
        </div>
      )}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-hero-title">{pageTitle}</h1>
          <p className="page-hero-subtitle">{pageSubtitle}</p>
        </div>
      </section>

      <section className="plans-section">
        <div className="container">
          <h2 className="plans-category-title">Programmes we offer</h2>
          <p className="plan-description" style={{ maxWidth: '760px', marginBottom: '2rem' }}>
            Sampoorna conducts educational programmes, workshops, webinars and courses so traditional knowledge can be accessible, structured and relevant to contemporary life.
          </p>
          <div className="plans-grid">
            {learningProgrammes.map((programme) => (
              <div key={programme.name} className="plan-card">
                <h3>{programme.name}</h3>
                <p className="plan-description">{programme.detail}</p>
                <button
                  className="btn btn-primary"
                  onClick={() => handleEnquiry(programme.name)}
                >
                  Send Enquiry
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plan Type Filter */}
      {plans.length > 0 && (
        <section className="plans-filter">
          <div className="container">
            <div className="filter-buttons">
              <button 
                className={selectedType === 'all' ? 'active' : ''}
                onClick={() => setSelectedType('all')}
              >
                All Plans
              </button>
              <button 
                className={selectedType === 'service_plan' ? 'active' : ''}
                onClick={() => setSelectedType('service_plan')}
              >
                Service Plans
              </button>
              <button 
                className={selectedType === 'course_plan' ? 'active' : ''}
                onClick={() => setSelectedType('course_plan')}
              >
                Course Plans
              </button>
              <button 
                className={selectedType === 'combo_plan' ? 'active' : ''}
                onClick={() => setSelectedType('combo_plan')}
              >
                Combo Plans
              </button>
            </div>
          </div>
        </section>
      )}

      {loading ? (
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Display plans grouped by type if showing all, or filtered plans */}
          {selectedType === 'all' ? (
            <>
              {/* Service Plans */}
              {groupedPlans.service_plan.length > 0 && (
                <section className="plans-section" id="service-plans">
                  <div className="container">
                    <h2 className="plans-category-title">Service Plans</h2>
                    <div className="plans-grid">
                      {groupedPlans.service_plan.map((plan) => (
                        <div key={plan.id} className={`plan-card ${plan.is_popular ? 'featured' : ''}`}>
                          {plan.is_popular && <div className="plan-badge">Popular</div>}
                          {plan.badge_text && <div className="plan-badge">{plan.badge_text}</div>}
                          <h3>{plan.plan_name}</h3>
                          <div className="plan-price">{formatPrice(plan)}</div>
                          {plan.short_description && <p className="plan-description">{plan.short_description}</p>}
                          <ul className="plan-features">
                            {(plan.features || getPlanItemsSummary(plan)).map((feature, idx) => (
                              <li key={idx}>✓ {typeof feature === 'string' ? feature : feature}</li>
                            ))}
                          </ul>
                          <button 
                            className="btn btn-primary" 
                            onClick={() => handleEnquiry(plan.plan_name)}
                          >
                            {plan.button_text || 'Send Enquiry'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {/* Course Plans */}
              {groupedPlans.course_plan.length > 0 && (
                <section className="plans-section" id="course-plans">
                  <div className="container">
                    <h2 className="plans-category-title">Course Plans</h2>
                    <div className="plans-grid">
                      {groupedPlans.course_plan.map((plan) => (
                        <div key={plan.id} className={`plan-card ${plan.is_popular ? 'featured' : ''}`}>
                          {plan.is_popular && <div className="plan-badge">Popular</div>}
                          {plan.badge_text && <div className="plan-badge">{plan.badge_text}</div>}
                          <h3>{plan.plan_name}</h3>
                          <div className="plan-price">{formatPrice(plan)}</div>
                          {plan.short_description && <p className="plan-description">{plan.short_description}</p>}
                          <ul className="plan-features">
                            {(plan.features || getPlanItemsSummary(plan)).map((feature, idx) => (
                              <li key={idx}>✓ {typeof feature === 'string' ? feature : feature}</li>
                            ))}
                          </ul>
                          <button 
                            className="btn btn-primary" 
                            onClick={() => handleEnquiry(plan.plan_name)}
                          >
                            {plan.button_text || 'Enroll Now'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {/* Combo Plans */}
              {groupedPlans.combo_plan.length > 0 && (
                <section className="plans-section" id="combo-plans">
                  <div className="container">
                    <h2 className="plans-category-title">Combo Plans</h2>
                    <div className="plans-grid">
                      {groupedPlans.combo_plan.map((plan) => (
                        <div key={plan.id} className={`plan-card ${plan.is_popular ? 'featured' : ''} ${plan.is_recommended ? 'recommended' : ''}`}>
                          {plan.is_popular && <div className="plan-badge">Popular</div>}
                          {plan.is_recommended && <div className="plan-badge recommended-badge">Recommended</div>}
                          {plan.badge_text && <div className="plan-badge">{plan.badge_text}</div>}
                          <h3>{plan.plan_name}</h3>
                          <div className="plan-price">{formatPrice(plan)}</div>
                          {plan.short_description && <p className="plan-description">{plan.short_description}</p>}
                          <ul className="plan-features">
                            {(plan.features || getPlanItemsSummary(plan)).map((feature, idx) => (
                              <li key={idx}>✓ {typeof feature === 'string' ? feature : feature}</li>
                            ))}
                          </ul>
                          <button 
                            className="btn btn-primary" 
                            onClick={() => handleEnquiry(plan.plan_name)}
                          >
                            {plan.button_text || 'Choose Plan'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}
            </>
          ) : (
            // Show filtered plans
            <section className="plans-section">
              <div className="container">
                <div className="plans-grid">
                  {displayPlans.map((plan) => (
                    <div key={plan.id} className={`plan-card ${plan.is_popular ? 'featured' : ''} ${plan.is_recommended ? 'recommended' : ''}`}>
                      {plan.is_popular && <div className="plan-badge">Popular</div>}
                      {plan.is_recommended && <div className="plan-badge recommended-badge">Recommended</div>}
                      {plan.badge_text && <div className="plan-badge">{plan.badge_text}</div>}
                      <h3>{plan.plan_name}</h3>
                      <div className="plan-price">{formatPrice(plan)}</div>
                      {plan.short_description && <p className="plan-description">{plan.short_description}</p>}
                      <ul className="plan-features">
                        {(plan.features || getPlanItemsSummary(plan)).map((feature, idx) => (
                          <li key={idx}>✓ {typeof feature === 'string' ? feature : feature}</li>
                        ))}
                      </ul>
                      <button 
                        className="btn btn-primary" 
                        onClick={() => handleEnquiry(plan.plan_name)}
                      >
                        {plan.button_text || 'Choose Plan'}
                      </button>
                    </div>
                  ))}
                </div>
                {displayPlans.length === 0 && (
                  <div className="text-center py-5">
                    <p className="text-muted">No plans available in this category.</p>
                  </div>
                )}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default Plans;
